/**
 * hook for getting unread announcements; manages data fetching and updates
 * @file useAnnouncementsData
 * @author Julius Diaz Panoriñgan
 */

import parse from 'html-react-parser'
import { useCallback, useEffect, useState } from 'react'
import sanitizeHtml from 'sanitize-html'

import { announcementsRef } from '../../firestore/refs'
import { rpc } from '../../utils/api'

import { useFirestoreValues } from '../firestore'

/**
 * hook that returns an iterator-like object for going through the announcements targeting a user
 * @function useAnnouncementsData
 * @param {Object} profile user profile, from which role and announcement status are read
 * @returns {Object}
 */
function useAnnouncementsData(profile) {
  const [announcementsForUser, setAnnouncementsForUser] = useState(null)
  const [announcementIndex, setAnnouncementIndex] = useState(-1)

  const [isReady, setIsReady] = useState(false) // a bit more comprehensive than an `isLoading` status

  // these are read/set exactly once
  const [todaysDate] = useState(new Date().toISOString().slice(0, 10))
  const [role] = useState(profile.role)
  const [announcementStatus] = useState(profile.announcementStatus || {})
  const [
    allAnnouncements,
    areAllAnnouncementsLoading,
    allAnnouncementsError
  ] = useFirestoreValues(announcementsRef(todaysDate, role), { get: true })

  useEffect(() => {
    if (allAnnouncementsError) {
      console.error(allAnnouncementsError)
    }
  }, [allAnnouncementsError])

  useEffect(() => {
    if (!areAllAnnouncementsLoading) {
      // filter out already-read announcements, transform for use below and by component
      const relevantAnnouncements = allAnnouncements
        ? allAnnouncements
            .filter(announcement => !announcementStatus[announcement.id])
            .map(announcement => ({
              content: parse(sanitizeForAnnouncement(announcement.content)),
              id: announcement.id,
              title: parse(sanitizeForAnnouncement(announcement.title))
            }))
        : []

      setAnnouncementsForUser(relevantAnnouncements)
      setIsReady(true)
      setAnnouncementIndex(0)
    }
  }, [allAnnouncements, announcementStatus, areAllAnnouncementsLoading])

  const isDone = isReady && announcementIndex >= announcementsForUser.length

  const announcement =
    isReady && !isDone ? announcementsForUser[announcementIndex] : null

  const nextAnnouncement = useCallback(() => {
    if (announcement) {
      setAnnouncementIndex(i => i + 1)

      rpc('user.markAnnouncementRead', {
        announcementId: announcement.id
      }).catch(e => {
        console.error(e)
      })
    }
  }, [announcement])

  // this hook can be effectively used with just `announcement` and `nextAnnouncement`;
  // that being said, `isDone` and `isReady` are available for more sophisticated state / UI/UX management
  return {
    announcement,
    isDone,
    isReady,
    nextAnnouncement
  }
}

export default useAnnouncementsData

/**
 * Sanitizes HTML markup for use in announcement modal
 * @function sanitizeForAnnouncement
 * @param {string} maybeDirty HTML markup
 * @returns {string} clean HTML markup
 */
function sanitizeForAnnouncement(maybeDirty) {
  return sanitizeHtml(maybeDirty, {
    allowedTags: ['a', 'b', 'em', 'i', 'p', 'strong'],
    allowedAttributes: {
      a: ['href', 'target']
    }
  })
}
